import { useMemo } from "react";
import { Chip } from "../../chip/Chip";
import { useFittedChips } from "../common/hooks/useFittedChips";
import { triggerChip } from "../common/Select.css";
import { Option } from "../common/SelectBase";
import {
  TriggerContentWrapper,
  TriggerText
} from "../common/TriggerContentWrapper";

export interface MultiSelectTriggerValueProps {
  /**
   * Array of selected option values
   */
  selectedValues: string[];
  /**
   * List of all available options
   */
  options: Option[];
  /**
   * Callback to toggle selection of an option
   */
  toggleOption: (value: string) => void;
  /**
   *  Placeholder text when no values are selected
   */
  placeholder?: string;
  /**
   * Whether the component is disabled
   */
  disabled?: boolean;
}

export function MultiTriggerValue({
  selectedValues,
  options,
  placeholder,
  disabled = false,
  toggleOption
}: MultiSelectTriggerValueProps): React.ReactElement {
  const optionByValue = useMemo(
    () => new Map(options.map((option) => [option.value, option])),
    [options]
  );
  const chips = selectedValues.map((value) => {
    const option = optionByValue.get(value);
    return {
      value,
      label: option?.label ?? value,
      locked: option?.disabled === true
    };
  });
  const { rowRef, visibleCount } = useFittedChips(
    // A locked chip drops its close icon, so it measures narrower.
    chips.map((chip) => `${chip.locked ? "-" : "+"}${chip.label}`)
  );
  const hiddenCount = chips.length - visibleCount;

  return (
    <TriggerContentWrapper rowRef={rowRef}>
      {!chips.length ? (
        <TriggerText text={placeholder} />
      ) : (
        <>
          {chips.slice(0, visibleCount).map((chip) => (
            <Chip
              key={chip.value}
              // A lone chip still shrinks, so a long label ellipsizes.
              className={visibleCount > 1 ? triggerChip : undefined}
              onClose={chip.locked ? undefined : () => toggleOption(chip.value)}
              insideButton
              disabled={disabled || chip.locked}
            >
              {chip.label}
            </Chip>
          ))}
          {hiddenCount > 0 && (
            <Chip className={triggerChip} disabled={disabled}>
              +{hiddenCount}
            </Chip>
          )}
        </>
      )}
    </TriggerContentWrapper>
  );
}
